/**
 * Pin / unpin.
 *
 * The pin is per MESSAGE, not per viewer: pinning an already-pinned message answers
 * 409, while unpinning an unpinned one succeeds silently (the office UI offers unpin to
 * every viewer, so double-clicks are routine). Both asymmetries are the server's
 * contract — this module surfaces them as one `togglePin` so callers never hold the
 * "is it already pinned?" question.
 *
 * A pin changes this conversation's own rows and nothing an inbox shows, so a pin
 * refreshes the conversation's queries (`useChannelInvalidate`) rather than the whole
 * `message.changed` set the route announces to other tabs.
 */

import { useCallback } from "react";

import {
  useCreateChannelsMessagesPinByCidByMessageId,
  useDeleteChannelsMessagesPinByCidByMessageId,
} from "./channel-endpoints";
import { useChannelInvalidate } from "./use-channel-invalidate";
import { useChannelMessageCache } from "./use-channel-message-cache";

export function useChannelPins(cid: string) {
  const invalidateChannel = useChannelInvalidate(cid);
  const patchMessage = useChannelMessageCache(cid);

  // Both routes answer the message's pin state, so the mark moves on the click rather
  // than when the refetch lands — and the refresh is NOT returned, so the unpin dialog
  // closes on the write instead of waiting out every mounted query.
  //
  // A patch can only change rows a cache already holds, so pinning does not put the
  // message on the strip; the refresh below does, a beat later. Nothing waits on that —
  // pinning is one-click with a toast and gates no dialog.
  const pinMutation = useCreateChannelsMessagesPinByCidByMessageId({
    onSuccess: (pin) => {
      patchMessage(pin.id, (message) => ({
        ...message,
        pinnedAt: pin.pinnedAt,
        pinnedById: pin.pinnedById,
      }));
      void invalidateChannel();
    },
  });
  // The strip's list is filtered by the SERVER, so clearing `pinnedAt` would leave the
  // row sitting on it: drop it there, clear the mark everywhere else.
  const unpinMutation = useDeleteChannelsMessagesPinByCidByMessageId({
    onSuccess: (pin) => {
      patchMessage(pin.id, (message, { pinnedOnly }) =>
        pinnedOnly ? null : { ...message, pinnedAt: null, pinnedById: null },
      );
      void invalidateChannel();
    },
  });

  const pinMessage = useCallback(
    (messageId: string) =>
      pinMutation.mutateAsync({ params: { cid, messageId } }),
    [pinMutation, cid],
  );

  const unpinMessage = useCallback(
    (messageId: string) =>
      unpinMutation.mutateAsync({ params: { cid, messageId } }),
    [unpinMutation, cid],
  );

  return {
    pinMessage,
    unpinMessage,
    isPinning: pinMutation.isPending || unpinMutation.isPending,
  };
}
